import React, { useReducer } from "react";

import axios from "axios";
import { useDispatch } from "react-redux";

import Alert from "@busbud/design-system-components/dist/Alert";
import Link from "@busbud/design-system-components/dist/Link";
import Stack from "@busbud/design-system-components/dist/Stack";
import { Button, Input } from "@busbud/horizon";

import { NoDiacriticsInput } from "@app/components/diacritics";
import { useAppContext } from "@app/helpers/hooks";
import type { NapiUser } from "@app/types/napi-types";

import type { AnyAction } from "@reduxjs/toolkit";

import * as Validator from "../../../../helpers/input-validator";
import addOnClickBehaviour from "../../../../lib/add-onclick-behaviour";
import { getErrorText } from "../../../../modules/account/utils";
import { logUserIn } from "../../../../modules/results/actions";
import {
  clickBackToSignin,
  confirmSignUp,
  signedUp,
  signupError
} from "../../../../tracking/account-tracking";
import { failedRequest } from "../../../../tracking/error-tracking";
import { useAnimation } from "../authentication.styles";

interface Field {
  value: string;
  has_error?: boolean;
  error?: string;
}

interface State {
  email: Field;
  first_name: Field;
  last_name: Field;
  password: Field;
  confirmed_password: Field;
  error?: string;
  is_submitting: boolean;
  verification_required?: boolean;
}

const getInitialState = (email?: string): State => ({
  email: { value: email || "" },
  first_name: { value: "" },
  last_name: { value: "" },
  password: { value: "" },
  confirmed_password: { value: "" },
  is_submitting: false
});

const reducer = (state: State, action: AnyAction): State => {
  switch (action.type) {
    case "error":
      return {
        ...state,
        [action.field]: {
          ...(state[action.field as keyof State] as any),
          has_error: true,
          error: action.error
        }
      };
    case "change":
      return {
        ...state,
        [action.field]: {
          value: action.value
        }
      };
    case "toggle":
      return {
        ...state,
        [action.field]: {
          value: action.value
        }
      };
    case "submit_form":
      return {
        ...state,
        error: undefined,
        is_submitting: true,
        verification_required: false
      };
    case "form_error": {
      return {
        ...state,
        error: action.error,
        is_submitting: false,
        verification_required: false
      };
    }
    case "verification_required": {
      return {
        ...state,
        error: undefined,
        is_submitting: false,
        verification_required: true
      };
    }
    default:
      return state;
  }
};

const fields_validation = [
  {
    name: "first_name",
    validationFn: (value: string) => Validator.firstName(value)
  },
  {
    name: "last_name",
    validationFn: (value: string) => Validator.lastName(value)
  },
  { name: "email", validationFn: (value: string) => Validator.email(value) },
  {
    name: "password",
    validationFn: (value: string) => Validator.signupPassword(value)
  },
  {
    name: "confirmed_password",
    validationFn: (value: string) => Validator.signupPassword(value)
  }
];

interface Props {
  email?: string;
  goToSignIn: () => void;
  redirect_url?: string;
  email_opt_in: boolean;
}

export const SignUpForm: React.FC<Props> = ({
  email: init_email,
  email_opt_in,
  goToSignIn,
  redirect_url
}) => {
  const { classes } = useAnimation();
  const { translator, locale, tracker, geoData, user_country_code } =
    useAppContext();
  const [state, dispatch] = useReducer(reducer, getInitialState(init_email));
  const reduxDispatch = useDispatch();

  const {
    email,
    first_name,
    last_name,
    password,
    confirmed_password,
    error,
    verification_required,
    is_submitting
  } = state;

  const hasError = () => {
    const has_error = fields_validation.some(({ name, validationFn }) => {
      const field_state: any = state[name as keyof State];
      const error = validationFn(field_state.value);
      if (error) {
        dispatch({
          type: "error",
          field: name,
          error: getErrorText(translator, `${name}.${error.name}`)
        });

        return true;
      }
    });

    if (password.value !== confirmed_password.value) {
      dispatch({
        type: "error",
        field: "confirmed_password",
        error: getErrorText(translator, "password.passwords_do_not_match")
      });
      return true;
    }

    return has_error;
  };

  const handleEmailSignUp = (event: React.MouseEvent) => {
    event.preventDefault();

    const auto_enrol_new_user =
      geoData.allowsImplicitConsentForMarketingEmails(user_country_code);

    tracker.track(confirmSignUp(undefined, auto_enrol_new_user));
    if (hasError()) {
      return;
    }

    dispatch({ type: "submit_form" });
    axios
      .post<NapiUser>("/auth/email-signup", {
        first_name: first_name.value,
        last_name: last_name.value,
        email: email.value,
        password: password.value,
        confirmed_password: confirmed_password.value,
        email_opt_in,
        locale,
        auto_enrol_new_user
      })
      .then(response => {
        tracker.track(signedUp(undefined, auto_enrol_new_user));
        reduxDispatch(logUserIn({ user: response.data, redirect_url }));
      })
      .catch(err => {
        const { response } = err;
        const error_type: string =
          response &&
          response.data &&
          response.data.error &&
          response.data.error.type;
        tracker.track(failedRequest(err, "signup", { error_type }));

        if (error_type === "UserEmailVerificationRequired") {
          return dispatch({ type: "verification_required" });
        }

        dispatch({
          type: "form_error",
          error: getErrorText(translator, error_type)
        });
        tracker.track(signupError(error_type));
      });
  };

  const handleChangeValue =
    (field: string) => (event: React.ChangeEvent<HTMLInputElement>) =>
      dispatch({
        type: "change",
        field,
        value: event.target.value
      });

  const getErrorHint = (field: Field) =>
    field.has_error && field.error ? field.error : "";

  const signin_message_with_action = addOnClickBehaviour(
    translator.t("!account.sign-up.email-does-not-exist"),
    () => {
      tracker.track(clickBackToSignin());
      goToSignIn();
    },
    props => <Link {...props} component="button" fontWeight="bold" />
  );

  return (
    <Stack spacing={2.5} direction="column" className={classes.container}>
      {
        // Redirect info when account does not exist
        !!init_email && (
          <Alert severity="informative">{signin_message_with_action}</Alert>
        )
      }
      {!!verification_required && (
        <Alert severity="positive">
          {translator.t("!account.sign-up.user-email-verification-required")}
        </Alert>
      )}
      {!!error && <Alert severity="negative">{error}</Alert>}
      <form autoComplete="on">
        <Stack spacing={2} direction="column">
          <Stack spacing={1} direction="column">
            <Input
              type="text"
              className="w-full"
              labelText={translator.t("!account.sign-up.first-name")}
              name="first_name"
              autoComplete="given-name"
              value={first_name.value}
              isInvalid={first_name.has_error}
              isDisabled={is_submitting}
              onChange={handleChangeValue("first_name")}
              helperText={getErrorHint(first_name)}
            />
            <Input
              type="text"
              className="w-full"
              labelText={translator.t("!account.sign-up.last-name")}
              name="last_name"
              autoComplete="family-name"
              value={last_name.value}
              isInvalid={last_name.has_error}
              isDisabled={is_submitting}
              onChange={handleChangeValue("last_name")}
              helperText={getErrorHint(last_name)}
            />
            <NoDiacriticsInput
              type="email"
              className="w-full"
              labelText={translator.t("!account.sign-up.email")}
              name="email"
              autoComplete="email"
              value={email.value}
              isInvalid={email.has_error}
              isDisabled={is_submitting}
              onChange={handleChangeValue("email")}
              helperText={getErrorHint(email)}
            />
            <Input
              type="password"
              className="w-full"
              labelText={translator.t("!account.sign-up.password")}
              name="password"
              autoComplete="new-password"
              value={password.value}
              isInvalid={password.has_error}
              isDisabled={is_submitting}
              onChange={handleChangeValue("password")}
              helperText={getErrorHint(password)}
            />
            <Input
              type="password"
              className="w-full"
              labelText={translator.t("!account.sign-up.confirm-password")}
              name="confirmed_password"
              autoComplete="new-password"
              value={confirmed_password.value}
              isInvalid={confirmed_password.has_error}
              isDisabled={is_submitting}
              onChange={handleChangeValue("confirmed_password")}
              helperText={getErrorHint(confirmed_password)}
            />
          </Stack>
          <Button
            className="w-full rounded-xs"
            id="submit"
            isSubmit
            variant="contained"
            appearance="primary"
            size="lg"
            isDisabled={is_submitting}
            onClick={handleEmailSignUp}
          >
            {translator.t("!account.sign-up.submit-button")}
          </Button>
        </Stack>
      </form>
    </Stack>
  );
};
